import { Facet } from "@linkfluence/models/facet";
import { Metric } from "@linkfluence/models/metric";
import {
  getAIDataviz,
  getBubblechartDataviz,
  getCloudDataviz,
  getDemographicDataviz,
  getDoughnutChartDataviz,
  getHorizontalBarChartDataviz,
  getHorizontalStackedBarChartDataviz,
  getImageClusteringDataviz,
  getKpiDataviz,
  getLineChartDataviz,
  getMapDataviz,
  getMultipleLineChartDataviz,
  getPictureWallDataviz,
  getStackedBarChartDataviz,
  getSunburstDataviz,
  getTopAuthorsDataviz,
  getTopPostsDataviz,
} from "@radarly/models/dataviz";
import {
  getWizardAICard,
  getWizardClassificationCard,
  getWizardClusterCard,
  getWizardDateMetricCard,
  getWizardDateTermCard,
  getWizardDemographicCard,
  getWizardGeoCard,
  getWizardPivotCard,
  getWizardPostsCard,
  getWizardTermCard,
  getWizardTermMetricsCard,
  getWizardUsersCard,
} from "@radarly/models/insight-pages/wizard/card";
import { WizardTemplate } from "@radarly/models/insight-pages/wizard/model";
import { WizardDateMetricCard } from "@radarly/models/insight-pages/wizard/model/card";
import { WizardConfigurationConditionField } from "@radarly/models/insight-pages/wizard/model/section";
import {
  WizardSectionBuilder,
  getWizardCardRow,
  getWizardTitleRow,
} from "@radarly/models/insight-pages/wizard/section";

const wizardBuilder = WizardSectionBuilder.getInstance();
const description = wizardBuilder.buildDescriptionSection;
const section = wizardBuilder.buildCardSection;
const loop = wizardBuilder.buildLoopCardsSection;
const title = getWizardTitleRow;
const row = getWizardCardRow;

function dateMetric(dataviz: WizardDateMetricCard["dataviz"]) {
  return function generateCard(
    props: Omit<Parameters<typeof getWizardDateMetricCard>[0], "dataviz">,
    clause?: WizardConfigurationConditionField
  ) {
    return getWizardDateMetricCard(
      {
        ...props,
        dataviz,
      },
      clause
    );
  };
}

const linechart = dateMetric(getLineChartDataviz());
const kpi = dateMetric(getKpiDataviz());

export const Benchmark_Template: WizardTemplate = {
  id: "benchmark",
  sections: [
    description(),
    section(
      [
        row([
          getWizardAICard({
            title: "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::GENERAL_ANALYSIS",
            analysis: {
              includeNegativeAnalysis: false,
              type: "general-analysis",
              useInsightPageAIContext: true,
            },
            dataviz: getAIDataviz(),
          }),
        ]),
        row([
          getWizardDateTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_BENCHMARK::BRAND_VOLUME_OVER_TIME",
            term: Facet.Focuses,
            metric: Metric.Repost,
            dataviz: getMultipleLineChartDataviz(),
          }),
        ]),
        row([
          getWizardTermMetricsCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_BENCHMARK::BRAND_RELATIVE_PERFORMANCE",
            term: Facet.Focuses,
            metrics: [Metric.Reach, Metric.Engagement, Metric.Repost],
            dataviz: getBubblechartDataviz(),
          }),
        ]),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_BENCHMARK::BRAND_SHARE_OF_VOICE",
            term: Facet.Focuses,
            metric: Metric.Repost,
            dataviz: getDoughnutChartDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_BENCHMARK::BRAND_AUDIENCE",
            term: Facet.Focuses,
            metric: Metric.Reach,
            dataviz: getDoughnutChartDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_BENCHMARK::BRAND_ENGAGEMENT",
            term: Facet.Focuses,
            metric: Metric.Engagement,
            dataviz: getDoughnutChartDataviz(),
          }),
        ]),
      ],
      ["queries"]
    ),
    loop(
      [
        title(["INSIGHT_PAGE_TEMPLATE_BENCHMARK::MOST_ENGAGING_CONTENT"], "h1"),
        getWizardAICard({
          title: "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::SIGNAL_ANALYSIS",
          analysis: {
            includeNegativeAnalysis: false,
            type: "signal-analysis",
            useInsightPageAIContext: true,
          },
          dataviz: getAIDataviz(),
        }),

        title(
          ["INSIGHT_PAGE_TEMPLATE_BENCHMARK::QUERY_MOST_ENG_PICTURES"],
          "h2"
        ),
        getWizardPostsCard({
          title: "::LOOP_ITEM::",
          sort: {
            on: Metric.Engagement,
          },
          dataviz: getPictureWallDataviz(),
        }),

        title(
          [
            "INSIGHT_PAGE_TEMPLATE_BENCHMARK::MOST_ENGAGING_PICTURES_SIMILARITY",
          ],
          "h2"
        ),
        getWizardClusterCard({
          title: "::LOOP_ITEM::",
          metric: Metric.Doc,
          dataviz: getImageClusteringDataviz(),
        }),
        title(
          [
            "INSIGHT_PAGE_TEMPLATE_BENCHMARK::QUERY_MOST_ENGAGING_NAMED_ENTITIES",
          ],
          "h2"
        ),
        getWizardTermCard({
          title: "::LOOP_ITEM::",
          term: Facet.Entities,
          metric: Metric.Engagement,
          dataviz: getCloudDataviz(),
        }),
        title(
          ["INSIGHT_PAGE_TEMPLATE_BENCHMARK::QUERY_MOST_ENGAGING_HASHTAGS"],
          "h1"
        ),
        getWizardTermCard({
          title: "::LOOP_ITEM::",
          term: Facet.Hashtags,
          metric: Metric.Engagement,
          dataviz: getCloudDataviz(),
        }),
        title(
          ["INSIGHT_PAGE_TEMPLATE_BENCHMARK::QUERY_EMERGING_HASHTAGS"],
          "h1"
        ),
        getWizardTermCard({
          title: "::LOOP_ITEM::",
          term: Facet.EmergingHashtags,
          metric: Metric.Doc,
          dataviz: getCloudDataviz(),
        }),
        title(["INSIGHT_PAGE_TEMPLATE_BENCHMARK::QUERY_TONE_SPLIT"], "h1"),
        getWizardTermCard({
          title: "::LOOP_ITEM::",
          term: Facet.Tones,
          metric: Metric.Engagement,
          dataviz: getDoughnutChartDataviz(),
        }),
        title(["INSIGHT_PAGE_TEMPLATE_BENCHMARK::QUERY_WHERE_TITLE"], "h1"),
        getWizardAICard({
          title: "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::AUDIENCE_ANALYSIS",
          analysis: {
            includeNegativeAnalysis: false,
            type: "audience-analysis",
            useInsightPageAIContext: true,
          },
          dataviz: getAIDataviz(),
        }),

        title(["INSIGHT_PAGE_TEMPLATE_BENCHMARK::QUERY_WHERE_PLATFORMS"], "h2"),
        getWizardTermCard({
          title: "::LOOP_ITEM::",
          term: Facet.Platforms,
          metric: Metric.Repost,
          dataviz: getDoughnutChartDataviz(),
        }),
        title(["INSIGHT_PAGE_TEMPLATE_BENCHMARK::QUERY_WHERE_COUNTRIES"], "h2"),
        getWizardGeoCard({
          title: "::LOOP_ITEM::",
          term: Facet.Countries,
          metric: Metric.Repost,
          dataviz: getHorizontalBarChartDataviz(),
        }),
        title(["INSIGHT_PAGE_TEMPLATE_BENCHMARK::QUERY_WHO_TITLE"], "h1"),
        title(["INSIGHT_PAGE_TEMPLATE_BENCHMARK::QUERY_WHO_DEMOGRAPHY"], "h2"),
        getWizardDemographicCard({
          title: "::LOOP_ITEM::",
          metric: Metric.Repost,
          dataviz: getDemographicDataviz(),
        }),
        title(["INSIGHT_PAGE_TEMPLATE_BENCHMARK::QUERY_WHO_LANGUAGES"], "h2"),
        getWizardTermCard({
          title: "::LOOP_ITEM::",
          term: Facet.Languages,
          metric: Metric.Repost,
          dataviz: getHorizontalBarChartDataviz(),
        }),
        title(["INSIGHT_PAGE_TEMPLATE_BENCHMARK::QUERY_WHO_OCCUPATIONS"], "h2"),
        getWizardTermCard({
          title: "::LOOP_ITEM::",
          term: Facet.Occupations,
          metric: Metric.Repost,
          dataviz: getCloudDataviz(),
        }),

        title(["INSIGHT_PAGE_TEMPLATE_BENCHMARK::QUERY_WHO_AUTHORS"], "h2"),
        getWizardUsersCard({
          title: "::LOOP_ITEM::",
          sort: Metric.Engagement,
          dataviz: getTopAuthorsDataviz(),
        }),
      ],
      "queries"
    ),
  ],
};

export const Brands_Benchmark_Template: WizardTemplate = {
  id: "brands_benchmark",
  sections: [
    section(
      [
        title(
          ["INSIGHT_PAGE_TEMPLATE_BRANDS_BENCHMARK::RELATIVE_PERFORMANCE"],
          "h1"
        ),
        row([
          kpi({
            title: "INSIGHT_PAGE_TEMPLATE_REPUTATION::KPI_METRIC_REPOST",
            metric: Metric.Repost,
          }),
          kpi({
            title: "INSIGHT_PAGE_TEMPLATE_REPUTATION::KPI_METRIC_REACH",
            metric: Metric.Reach,
          }),
          kpi({
            title: "INSIGHT_PAGE_TEMPLATE_REPUTATION::KPI_METRIC_ENGAGEMENT",
            metric: Metric.Engagement,
          }),
        ]),
        row([
          getWizardTermMetricsCard({
            title: "INSIGHT_PAGE_TEMPLATE_BRANDS_OVERVIEW::PLATFORMS",
            term: Facet.Focuses,
            metrics: [Metric.Reach, Metric.Engagement, Metric.Repost],
            dataviz: getBubblechartDataviz(),
          }),
        ]),
        title(["INSIGHT_PAGE_TEMPLATE_BRANDS_BENCHMARK::MOST_IMPACTFUL"], "h1"),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_BENCHMARK::BRAND_AWARENESS",
            term: Facet.Focuses,
            metric: Metric.Repost,
            dataviz: getHorizontalBarChartDataviz({
              mainUnit: "percentage",
              secondaryUnit: "evolution",
            }),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_BENCHMARK::BRAND_DESIRABILITY",
            term: Facet.Focuses,
            metric: Metric.Repost,
            clause: { and: [{ tones: ["positive"] }] },
            dataviz: getHorizontalBarChartDataviz({
              mainUnit: "percentage",
              secondaryUnit: "evolution",
            }),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_BENCHMARK::BRAND_PROXIMITY",
            term: Facet.Focuses,
            metric: Metric.Engagement,
            dataviz: getHorizontalBarChartDataviz({
              mainUnit: "percentage",
              secondaryUnit: "evolution",
            }),
          }),
        ]),
      ],
      ["queries"]
    ),
    loop(
      [
        title(
          ["INSIGHT_PAGE_TEMPLATE_BRANDS_BENCHMARK::QUERY_RELEVANCE"],
          "h1"
        ),
        getWizardTermCard({
          title: "::LOOP_ITEM::",
          term: Facet.Focuses,
          metric: Metric.Repost,
          clause: { and: [{ customFields: ["drivers"] }] },
          dataviz: getHorizontalBarChartDataviz(),
        }),
      ],
      "queries"
    ),
  ],
};

export const Content_Template: WizardTemplate = {
  id: "content",
  sections: [
    section(
      [
        row([
          getWizardAICard({
            title: "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::GENERAL_ANALYSIS",
            analysis: {
              includeNegativeAnalysis: false,
              type: "general-analysis",
              useInsightPageAIContext: true,
            },
            dataviz: getAIDataviz(),
          }),
        ]),
        row(
          [
            kpi({
              title: "INSIGHT_PAGE_TEMPLATE_CONTENT::VOLUME_POSTS",
              metric: Metric.Repost,
            }),
            linechart({
              title: "INSIGHT_PAGE_TEMPLATE_CONTENT::VOLUME_OVER_TIME",
              metric: Metric.Repost,
            }),
          ],
          "right"
        ),
        title(
          ["INSIGHT_PAGE_TEMPLATE_CONTENT::WHO_IS_AUDIENCE_DISCUSSING"],
          "h1"
        ),
        row([
          getWizardAICard({
            title: "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::AUDIENCE_ANALYSIS",
            analysis: {
              includeNegativeAnalysis: false,
              type: "audience-analysis",
              useInsightPageAIContext: true,
            },
            dataviz: getAIDataviz(),
          }),
        ]),
        row([
          getWizardDemographicCard({
            title: "INSIGHT_PAGE_TEMPLATE_CONTENT::AUDIENCES::DEMOGRAPHIC",
            metric: Metric.Repost,
            dataviz: getDemographicDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CONTENT::AUDIENCES::OCCUPATIONS",
            term: Facet.Occupations,
            metric: Metric.Repost,
            dataviz: getCloudDataviz(),
          }),
        ]),
        title(["INSIGHT_PAGE_TEMPLATE_CONTENT::CONVERSATION_TOPICS"], "h1"),
        row([
          getWizardAICard({
            title: "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::SIGNAL_ANALYSIS",
            analysis: {
              includeNegativeAnalysis: false,
              type: "signal-analysis",
              useInsightPageAIContext: true,
            },
            dataviz: getAIDataviz(),
          }),
        ]),
        title(["INSIGHT_PAGE_TEMPLATE_CONTENT::OVERVIEW"], "h2"),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CONTENT::PLATFORMS_SHARE",
            term: Facet.Platforms,
            metric: Metric.Repost,
            dataviz: getDoughnutChartDataviz(),
          }),
          getWizardClassificationCard({
            title: "INSIGHT_PAGE_TEMPLATE_CONTENT::CONVERSATIONS_TOPICS",
            metric: Metric.Repost,
            dataviz: getCloudDataviz(),
          }),
        ]),
        title(["INSIGHT_PAGE_TEMPLATE_CONTENT::ENGAGING_INSPIRATION"], "h2"),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CONTENT::HASHTAGS",
            term: Facet.Hashtags,
            metric: Metric.Engagement,
            dataviz: getCloudDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CONTENT::KEYPHRASES",
            term: Facet.Keyphrases,
            metric: Metric.Engagement,
            dataviz: getCloudDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CONTENT::MENTIONS",
            term: Facet.Mentions,
            metric: Metric.Engagement,
            dataviz: getCloudDataviz(),
          }),
        ]),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CONTENT::EMERGING_AFFECTS",
            term: Facet.Affects,
            metric: Metric.Engagement,
            dataviz: getCloudDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CONTENT::EMOJIS",
            term: Facet.EmojiCharts,
            metric: Metric.Engagement,
            dataviz: getCloudDataviz(),
          }),
        ]),
        row([
          getWizardPostsCard({
            title: "INSIGHT_PAGE_TEMPLATE_CONTENT::MOST_ENGAGING_PICTURES",
            sort: {
              on: Metric.Engagement,
            },
            dataviz: getPictureWallDataviz(),
          }),
        ]),
        row([
          getWizardUsersCard({
            title: "INSIGHT_PAGE_TEMPLATE_CONTENT::TOP_AUTHORS",
            sort: Metric.Engagement,
            dataviz: getTopAuthorsDataviz(),
          }),
          getWizardPostsCard({
            title: "INSIGHT_PAGE_TEMPLATE_CONTENT::TOP_POSTS",
            sort: { on: Metric.Engagement },
            dataviz: getTopPostsDataviz(),
          }),
        ]),
        title(["INSIGHT_PAGE_TEMPLATE_CONTENT::EMERGING"], "h2"),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CONTENT::EMERGING_HASHTAGS",
            term: Facet.EmergingHashtags,
            metric: Metric.Repost,
            dataviz: getCloudDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CONTENT::EMERGING_KEYPHRASES",
            term: Facet.EmergingKeyphrases,
            metric: Metric.Repost,
            dataviz: getCloudDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CONTENT::EMERGING_MENTIONS",
            term: Facet.EmergingMentions,
            metric: Metric.Repost,
            dataviz: getCloudDataviz(),
          }),
        ]),
      ],
      ["queries"]
    ),
  ],
};

export const Customer_Experience_Template: WizardTemplate = {
  id: "customer_experience",
  sections: [
    section(
      [
        row(
          [
            getWizardDateMetricCard({
              title: "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::VOLUME_REVIEWS",
              metric: Metric.Repost,
              dataviz: getKpiDataviz({ displayDistributions: false }),
            }),
            linechart({
              title: "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::REVIEWS_OVER_TIME",
              metric: Metric.Repost,
            }),
          ],
          "right"
        ),
        row([
          getWizardUsersCard({
            title: "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::TOP_SITES",
            sort: Metric.Doc,
            dataviz: getTopAuthorsDataviz(),
            clause: {
              and: [{ platforms: ["review"] }],
            },
          }),
        ]),
        title(
          ["INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::REVIEW_RATING_DETAILS"],
          "h1"
        ),
        title(
          ["INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::REVIEW_RATING_TEXT"],
          "text"
        ),
        row(
          [
            getWizardTermCard({
              title: "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::RATINGS_DISTRIBUTION",
              term: Facet.ReviewRate,
              metric: Metric.Repost,
              dataviz: getHorizontalBarChartDataviz(),
            }),
            getWizardDateTermCard({
              title: "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::RATINGS_OVER_TIME",
              term: Facet.ReviewRate,
              metric: Metric.Repost,
              dataviz: getStackedBarChartDataviz(),
            }),
          ],
          "right"
        ),
        title(
          ["INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::REVIEW_STORE_RANKINGS"],
          "h1"
        ),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::MOST_REVIEWED_STORES",
            term: Facet.Focuses,
            metric: Metric.Repost,
            dataviz: getHorizontalBarChartDataviz({ sort: "mainUnit" }),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::BEST_RATED_STORES",
            term: Facet.Focuses,
            metric: Metric.Rating,
            dataviz: getHorizontalBarChartDataviz({
              displayNullValues: false,
              color: "#00AE9E",
              order: "desc",
            }),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::WORST_RATED_STORES",
            term: Facet.Focuses,
            metric: Metric.Rating,
            dataviz: getHorizontalBarChartDataviz({
              displayNullValues: false,
              color: "#E64C65",
              order: "asc",
            }),
          }),
        ]),
        title(["INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::REVIEW_SENTIMENTS"], "h1"),
        title(["INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::SENTIMENTS_OVERVIEW"], "h2"),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::SENTIMENT",
            term: Facet.Tones,
            metric: Metric.Repost,
            dataviz: getDoughnutChartDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::AFFECTS",
            term: Facet.Affects,
            metric: Metric.Repost,
            dataviz: getCloudDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::EMOTIONS",
            term: Facet.Emotions,
            metric: Metric.Repost,
            dataviz: getCloudDataviz(),
          }),
        ]),
        title(["INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::REVIEW_MOOD"], "h2"),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::AFFECTS_1_2*",
            term: Facet.Affects,
            metric: Metric.Repost,
            clause: { and: [{ rating: { gt: 0, lt: 50 } }] },
            dataviz: getCloudDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::AFFECTS_3*",
            term: Facet.Affects,
            metric: Metric.Repost,
            clause: { and: [{ rating: { gt: 50.1, lt: 70 } }] },
            dataviz: getCloudDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::AFFECTS_5*",
            term: Facet.Affects,
            metric: Metric.Repost,
            clause: { and: [{ rating: { gt: 69, lt: 100 } }] },
            dataviz: getCloudDataviz(),
          }),
        ]),
        title(["INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::KEYWORD"], "h1"),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::KEYPHRASES_1_2*",
            term: Facet.Keyphrases,
            metric: Metric.Repost,
            clause: { and: [{ rating: { gt: 0, lt: 50 } }] },
            dataviz: getCloudDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::KEYPHRASES_3*",
            term: Facet.Keyphrases,
            metric: Metric.Repost,
            clause: { and: [{ rating: { gt: 50.1, lt: 70 } }] },
            dataviz: getCloudDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::KEYPHRASES_4_5*",
            term: Facet.Keyphrases,
            metric: Metric.Repost,
            clause: { and: [{ rating: { gt: 69, lt: 100 } }] },
            dataviz: getCloudDataviz(),
          }),
        ]),
        title(["INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::REVIEW-SITES"], "h1"),
        row([
          getWizardUsersCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::REVIEW-SITES_AUTHORS_1_2*",
            sort: Metric.Rating,
            clause: {
              and: [{ rating: { gt: 0, lt: 50 } }],
            },
            dataviz: getTopAuthorsDataviz(),
          }),
          getWizardUsersCard({
            title: "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::REVIEW-SITES_AUTHORS_3*",
            sort: Metric.Rating,
            clause: {
              and: [{ rating: { gt: 50.1, lt: 70 } }],
            },
            dataviz: getTopAuthorsDataviz(),
          }),
          getWizardUsersCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_CUSTOMER_XP::REVIEW-SITES_AUTHORS_4_5*",
            sort: Metric.Rating,
            clause: {
              and: [{ rating: { gt: 69, lt: 100 } }],
            },
            dataviz: getTopAuthorsDataviz(),
          }),
        ]),
      ],
      ["queries"]
    ),
  ],
};

export const Reputation_Template: WizardTemplate = {
  id: "reputation",
  sections: [
    description(),
    section(
      [
        title(["INSIGHT_PAGE_TEMPLATE_REPUTATION::BRAND_AWARENESS"], "h1"),
        row([
          getWizardAICard({
            title: "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::GENERAL_ANALYSIS",
            analysis: {
              includeNegativeAnalysis: false,
              type: "general-analysis",
              useInsightPageAIContext: true,
            },
            dataviz: getAIDataviz(),
          }),
        ]),
        row([
          kpi({
            title: "INSIGHT_PAGE_TEMPLATE_REPUTATION::KPI_METRIC_REPOST",
            metric: Metric.Repost,
          }),
          kpi({
            title: "INSIGHT_PAGE_TEMPLATE_REPUTATION::KPI_METRIC_REACH",
            metric: Metric.Reach,
          }),
          kpi({
            title: "INSIGHT_PAGE_TEMPLATE_REPUTATION::KPI_METRIC_ENGAGEMENT",
            metric: Metric.Engagement,
          }),
        ]),
        row(
          [
            getWizardTermCard({
              title: "INSIGHT_PAGE_TEMPLATE_REPUTATION::POSTS_PLATFORMS",
              term: Facet.Platforms,
              metric: Metric.Repost,
              dataviz: getHorizontalBarChartDataviz(),
            }),

            linechart({
              title: "INSIGHT_PAGE_TEMPLATE_CONTENT::VOLUME_OVER_TIME",
              metric: Metric.Doc,
            }),
          ],
          "right"
        ),
      ],
      ["query"]
    ),
    section(
      [
        title(
          ["INSIGHT_PAGE_TEMPLATE_REPUTATION::RELATED_TOPICS::TITLE"],
          "h1"
        ),
        row([
          getWizardTermCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_REPUTATION::RELATED_TOPICS::DISTRIBUTION",
            term: Facet.ContentClassification,
            metric: Metric.Repost,
            dataviz: getHorizontalBarChartDataviz(),
          }),
          getWizardClassificationCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_REPUTATION::RELATED_TOPICS::CATEGORIES",
            metric: Metric.Repost,
            dataviz: getSunburstDataviz(),
          }),
        ]),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_REPUTATION::RELATED_TOPICS::HASHTAGS",
            term: Facet.Hashtags,
            metric: Metric.Repost,
            dataviz: getCloudDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_REPUTATION::RELATED_TOPICS::EMOJIS",
            term: Facet.EmojiCharts,
            metric: Metric.Repost,
            dataviz: getCloudDataviz(),
          }),
        ]),
        title(["INSIGHT_PAGE_TEMPLATE_REPUTATION::HOT_TOPICS::TITLE"], "h1"),
        row([
          getWizardAICard({
            title: "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::SIGNAL_ANALYSIS",
            analysis: {
              includeNegativeAnalysis: false,
              type: "signal-analysis",
              useInsightPageAIContext: true,
            },
            dataviz: getAIDataviz(),
          }),
        ]),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CONTENT::EMERGING_KEYPHRASES",
            term: Facet.EmergingKeyphrases,
            metric: Metric.Doc,
            dataviz: getCloudDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_BENCHMARK::QUERY_EMERGING_HASHTAGS",
            term: Facet.EmergingHashtags,
            metric: Metric.Doc,
            dataviz: getCloudDataviz(),
          }),
          getWizardTermCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_REPUTATION::HOT_TOPICS::EMERGING_NAMED_ENTITIES",
            term: Facet.EmergingNamedEntities,
            metric: Metric.Doc,
            dataviz: getCloudDataviz(),
          }),
        ]),
        title(
          [
            "INSIGHT_PAGE_TEMPLATE_REPUTATION::IMPACTFUL_PICTURES_VIDEOS::TITLE",
          ],
          "h1"
        ),
        row([
          getWizardPostsCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_REPUTATION::IMPACTFUL_PICTURES_VIDEOS::MOST_ENGAGING",
            sort: {
              on: Metric.Engagement,
            },
            dataviz: getPictureWallDataviz(),
          }),
        ]),
        row([
          getWizardClusterCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_REPUTATION::IMPACTFUL_PICTURES_VIDEOS::SIMILARITY",
            metric: Metric.Doc,
            dataviz: getImageClusteringDataviz(),
          }),
        ]),
      ],
      ["query"]
    ),
    section(
      [
        title(["INSIGHT_PAGE_TEMPLATE_REPUTATION::CELEBRITIES::TITLE"], "h1"),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_REPUTATION::CELEBRITIES::RECOGNITION",
            term: Facet.ImageRecognitionPersonIdentifications,
            metric: Metric.Doc,
            dataviz: getHorizontalBarChartDataviz(),
          }),
          getWizardPostsCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_REPUTATION::CELEBRITIES::MOST_ENGAGING",
            sort: {
              on: Metric.Engagement,
            },
            clause: {
              and: [{ [Facet.Media]: ["image", "video"] }],
            },
            dataviz: getPictureWallDataviz(),
          }),
        ]),
      ],
      ["query", "personIdentifications"]
    ),
    section(
      [
        title(
          ["INSIGHT_PAGE_TEMPLATE_REPUTATION::RELATED_SENTIMENT::TITLE"],
          "h1"
        ),
        row([
          getWizardAICard({
            title: "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::RISK_ANALYSIS",
            analysis: {
              includeNegativeAnalysis: false,
              type: "risk-analysis",
              useInsightPageAIContext: true,
            },
            dataviz: getAIDataviz(),
          }),
        ]),
        row([
          getWizardTermCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_REPUTATION::RELATED_SENTIMENT::PIECHART",
            term: Facet.Tones,
            metric: Metric.Repost,
            dataviz: getDoughnutChartDataviz(),
          }),
          getWizardDateTermCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_REPUTATION::RELATED_SENTIMENT::STACKED_BARCHART",
            term: Facet.Tones,
            metric: Metric.Repost,
            dataviz: getStackedBarChartDataviz({
              normalized: true,
            }),
          }),
        ]),
        row([
          getWizardPivotCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_REPUTATION::RELATED_SENTIMENT::PLATFORM",
            term: Facet.Platforms,
            metric: Metric.Repost,
            pivot: Facet.Tones,
            dataviz: getHorizontalStackedBarChartDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_REPUTATION::EMOTIONS",
            term: Facet.Emotions,
            metric: Metric.Doc,
            dataviz: getHorizontalBarChartDataviz(),
          }),
        ]),
        title(["INSIGHT_PAGE_TEMPLATE_REPUTATION::RISK::TITLE"], "h1"),
        row([
          getWizardPostsCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_REPUTATION::RISK::MOST_NEGATIVE_ENGAGING_POSTS",
            sort: {
              on: Metric.Engagement,
            },
            clause: {
              and: [{ [Facet.Tones]: ["negative"] }],
            },
            dataviz: getTopPostsDataviz(),
          }),
          getWizardTermCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_REPUTATION::RISK::MOST_FREQUENT_NEGATIVE_HASHTAGS",
            term: Facet.Hashtags,
            metric: Metric.Repost,
            clause: {
              and: [{ [Facet.Tones]: ["negative"] }],
            },
            dataviz: getCloudDataviz({ color: "#E64C65" }),
          }),
          getWizardTermCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_REPUTATION::RISK::EMERGING_NEGATIVE_HASHTAGS",
            term: Facet.EmergingHashtags,
            metric: Metric.Repost,
            clause: {
              and: [{ [Facet.Tones]: ["negative"] }],
            },
            dataviz: getCloudDataviz({ color: "#E64C65" }),
          }),
        ]),
        title(["INSIGHT_PAGE_TEMPLATE_REPUTATION::AUDIENCES::NEW_TITLE"], "h1"),
        row([
          getWizardAICard({
            title: "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::AUDIENCE_ANALYSIS",
            analysis: {
              includeNegativeAnalysis: false,
              type: "audience-analysis",
              useInsightPageAIContext: true,
            },
            dataviz: getAIDataviz(),
          }),
        ]),
        row([
          getWizardGeoCard({
            title: "INSIGHT_PAGE_TEMPLATE_REPUTATION::AUDIENCES::COUNTRIES",
            term: Facet.Countries,
            metric: Metric.Repost,
            dataviz: getMapDataviz(),
          }),
          getWizardDemographicCard({
            title: "INSIGHT_PAGE_TEMPLATE_REPUTATION::AUDIENCES::DEMOGRAPHIC",
            metric: Metric.Repost,
            dataviz: getDemographicDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_REPUTATION::AUDIENCES::OCCUPATIONS",
            term: Facet.Occupations,
            metric: Metric.Repost,
            dataviz: getCloudDataviz(),
          }),
        ]),
        row([
          getWizardUsersCard({
            title: "INSIGHT_PAGE_TEMPLATE_REPUTATION::AUDIENCES::AUTHORS",
            sort: Metric.Engagement,
            dataviz: getTopAuthorsDataviz(),
          }),
        ]),
      ],
      ["query"]
    ),
  ],
};

export const Campaign_Template: WizardTemplate = {
  id: "campaign",
  sections: [
    description(),
    section(
      [
        title(["INSIGHT_PAGE_TEMPLATE_CAMPAIGN::GLOBAL_OVERVIEW::TITLE"], "h2"),
        row([
          getWizardAICard({
            title: "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::GENERAL_ANALYSIS",
            analysis: {
              includeNegativeAnalysis: false,
              type: "general-analysis",
              useInsightPageAIContext: true,
            },
            dataviz: getAIDataviz(),
          }),
        ]),
        row([
          kpi({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::GLOBAL_OVERVIEW::KPI_METRIC_REPOST",
            metric: Metric.Repost,
          }),
          kpi({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::GLOBAL_OVERVIEW::KPI_METRIC_REACH",
            metric: Metric.Reach,
          }),
          kpi({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::GLOBAL_OVERVIEW::KPI_METRIC_ENGAGEMENT",
            metric: Metric.Engagement,
          }),
        ]),
        row([
          linechart({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::GLOBAL_OVERVIEW::VOLUME_OVER_TIME_METRIC_REPOST",
            metric: Metric.Repost,
          }),
        ]),
        row([
          getWizardPostsCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::GLOBAL_OVERVIEW::MOST_ENGAGING_IMAGES_AND_VIDEOS",
            sort: {
              on: Metric.Engagement,
            },
            dataviz: getPictureWallDataviz(),
          }),
        ]),
        row([
          getWizardClusterCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_REPUTATION::IMPACTFUL_PICTURES_VIDEOS::SIMILARITY",
            metric: Metric.Doc,
            dataviz: getImageClusteringDataviz(),
          }),
        ]),
        title(
          ["INSIGHT_PAGE_TEMPLATE_CAMPAIGN::MOST_INFLUENTIAL_CONTENT::TITLE"],
          "h2"
        ),
        row([
          getWizardTermCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::MOST_INFLUENTIAL_CONTENT::METRIC_REPOST",
            term: Facet.Platforms,
            metric: Metric.Repost,
            dataviz: getDoughnutChartDataviz(),
          }),
          getWizardTermCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::MOST_INFLUENTIAL_CONTENT::METRIC_REACH",
            term: Facet.Platforms,
            metric: Metric.Reach,
            dataviz: getDoughnutChartDataviz(),
          }),
          getWizardTermCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::MOST_INFLUENTIAL_CONTENT::METRIC_ENGAGEMENT",
            term: Facet.Platforms,
            metric: Metric.Engagement,
            dataviz: getDoughnutChartDataviz(),
          }),
        ]),
      ],
      ["query"]
    ),

    section(
      [
        title(["INSIGHT_PAGE_TEMPLATE_CAMPAIGN::OWNED_MEDIA::TITLE"], "h1"),
        title(
          ["INSIGHT_PAGE_TEMPLATE_CAMPAIGN::OWNED_MEDIA::IMPACT_TITLE"],
          "h2"
        ),
        row([
          kpi({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::OWNED_MEDIA::IMPACT_KPI_METRIC_REPOST",
            metric: Metric.Repost,
          }),
          kpi({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::OWNED_MEDIA::IMPACT_KPI_METRIC_REACH",
            metric: Metric.Reach,
          }),
          kpi({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::OWNED_MEDIA::IMPACT_KPI_METRIC_ENGAGEMENT",
            metric: Metric.Engagement,
          }),
        ]),
        title(
          ["INSIGHT_PAGE_TEMPLATE_CAMPAIGN::OWNED_MEDIA::MOST_IMPACTFUL"],
          "h2"
        ),
        row([
          getWizardUsersCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::OWNED_MEDIA::MOST_ENGAGING_AUTHORS",
            sort: Metric.Engagement,
            dataviz: getTopAuthorsDataviz(),
          }),
          getWizardPostsCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::OWNED_MEDIA::MOST_ENGAGING_OWNED_POSTS",
            sort: {
              on: Metric.Engagement,
            },
            dataviz: getTopPostsDataviz(),
          }),
        ]),
      ],
      ["query", "owned"]
    ),
    section(
      [
        title(
          ["INSIGHT_PAGE_TEMPLATE_CAMPAIGN::INFLUENCERS_PERFORMANCE::TITLE"],
          "h1"
        ),
        title(
          ["INSIGHT_PAGE_TEMPLATE_CAMPAIGN::OWNED_MEDIA::AMBASSADORS_IMPACT"],
          "h2"
        ),
        row([
          getWizardUsersCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::OWNED_MEDIA::MOST_ENGAGING_INFLUENCERS",
            sort: Metric.Engagement,
            dataviz: getTopAuthorsDataviz(),
          }),
          getWizardPostsCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_REPUTATION::RISK::MOST_ENGAGING_POSTS",
            sort: {
              on: Metric.Engagement,
            },
            dataviz: getTopPostsDataviz(),
          }),
        ]),
      ],
      ["query", "influencers"]
    ),
    section(
      [
        title(["INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::TITLE"], "h1"),
        title(
          ["INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::IMPACT_TITLE"],
          "h2"
        ),
        row([
          getWizardAICard({
            title: "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::SIGNAL_ANALYSIS",
            analysis: {
              includeNegativeAnalysis: false,
              type: "signal-analysis",
              useInsightPageAIContext: true,
            },
            dataviz: getAIDataviz(),
          }),
        ]),
        row([
          kpi({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::EARNED_KPI_METRIC_REPOST",
            metric: Metric.Repost,
          }),
          kpi({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::EARNED_KPI_METRIC_REACH",
            metric: Metric.Reach,
          }),
          kpi({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::EARNED_KPI_METRIC_ENGAGEMENT",
            metric: Metric.Engagement,
          }),
        ]),

        title(
          ["INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::REACTIONS"],
          "h2"
        ),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::SENTIMENT",
            term: Facet.Tones,
            metric: Metric.Repost,
            dataviz: getDoughnutChartDataviz(),
          }),
          getWizardPivotCard({
            title: "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::PLATFORM",
            term: Facet.Platforms,
            pivot: Facet.Tones,
            metric: Metric.Repost,
            dataviz: getHorizontalStackedBarChartDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::EMOTIONS",
            term: Facet.Emotions,
            metric: Metric.Repost,
            dataviz: getHorizontalBarChartDataviz(),
          }),
        ]),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::KEYPHRASES",
            term: Facet.Keyphrases,
            metric: Metric.Repost,
            dataviz: getCloudDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::HASHTAGS",
            term: Facet.Hashtags,
            metric: Metric.Repost,
            dataviz: getCloudDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::EMOJIS",
            term: Facet.EmojiCharts,
            metric: Metric.Repost,
            dataviz: getCloudDataviz(),
          }),
        ]),
        row([
          getWizardPostsCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::MOST_ENGAGING_PICTURES",
            sort: {
              on: Metric.Engagement,
            },
            dataviz: getPictureWallDataviz(),
          }),
        ]),
        title(
          ["INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::SPOKESPEOPLE"],
          "h2"
        ),
        row([
          getWizardAICard({
            title: "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::AUDIENCE_ANALYSIS",
            analysis: {
              includeNegativeAnalysis: false,
              type: "audience-analysis",
              useInsightPageAIContext: true,
            },
            dataviz: getAIDataviz(),
          }),
        ]),
        row([
          getWizardDemographicCard({
            title: "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::DEMOGRAPHICS",
            metric: Metric.Repost,
            dataviz: getDemographicDataviz(),
          }),
          getWizardGeoCard({
            title: "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::COUNTRIES",
            term: Facet.Countries,
            metric: Metric.Repost,
            dataviz: getMapDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::OCCUPATIONS",
            term: Facet.Occupations,
            metric: Metric.Repost,
            dataviz: getCloudDataviz(),
          }),
        ]),
        title(
          ["INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::AMBASSADORS"],
          "h2"
        ),
        row([
          getWizardUsersCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::MOST_ENGAGING_AUTHORS",
            sort: Metric.Engagement,
            dataviz: getTopAuthorsDataviz(),
          }),
          getWizardPostsCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_CAMPAIGN::EARNED_MEDIA::MOST_ENGAGING_EARNED_POSTS",
            sort: {
              on: Metric.Engagement,
            },
            dataviz: getTopPostsDataviz(),
          }),
        ]),
      ],
      ["query", "earned"],
      { with: ["owned"] }
    ),
  ],
};

export const Brands_Overview_Template: WizardTemplate = {
  id: "brands_overview",
  sections: [
    description(),
    section(
      [
        title(["INSIGHT_PAGE_TEMPLATE_BRANDS_OVERVIEW::BRAND_AWARENESS"], "h1"),
        row([
          kpi({
            title: "INSIGHT_PAGE_TEMPLATE_BRANDS_OVERVIEW::KPI_METRIC_REPOST",
            metric: Metric.Repost,
          }),
          kpi({
            title: "INSIGHT_PAGE_TEMPLATE_BRANDS_OVERVIEW::KPI_METRIC_REACH",
            metric: Metric.Reach,
          }),
          kpi({
            title:
              "INSIGHT_PAGE_TEMPLATE_BRANDS_OVERVIEW::KPI_METRIC_ENGAGEMENT",
            metric: Metric.Engagement,
          }),
        ]),
        row([
          getWizardPostsCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_BRANDS_OVERVIEW::MOST_ENGAGING_PICTURES",
            sort: {
              on: Metric.Engagement,
            },
            dataviz: getPictureWallDataviz(),
          }),
        ]),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_BRANDS_OVERVIEW::BRANDS_RANKING",
            term: Facet.Focuses,
            metric: Metric.Repost,
            dataviz: getHorizontalBarChartDataviz(),
          }),
        ]),
        title(["INSIGHT_PAGE_TEMPLATE_BRANDS_OVERVIEW::POPULAR_CONTENT"], "h1"),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_BRANDS_OVERVIEW::HASHTAGS",
            term: Facet.Hashtags,
            metric: Metric.Repost,
            dataviz: getCloudDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_BRANDS_OVERVIEW::KEYWORDS",
            term: Facet.Entities,
            metric: Metric.Repost,
            dataviz: getCloudDataviz(),
          }),
        ]),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_BRANDS_OVERVIEW::PLATFORMS",
            term: Facet.Platforms,
            metric: Metric.Repost,
            dataviz: getDoughnutChartDataviz(),
          }),
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_BRANDS_OVERVIEW::TONES",
            term: Facet.Tones,
            metric: Metric.Repost,
            dataviz: getDoughnutChartDataviz(),
          }),
        ]),
        title(["INSIGHT_PAGE_TEMPLATE_BRANDS_OVERVIEW::AUDIENCE"], "h1"),
        row([
          getWizardDemographicCard({
            title: "INSIGHT_PAGE_TEMPLATE_BRANDS_OVERVIEW::DEMOGRAPHICS",
            metric: Metric.Repost,
            dataviz: getDemographicDataviz(),
          }),
          getWizardGeoCard({
            title: "INSIGHT_PAGE_TEMPLATE_BRANDS_OVERVIEW::COUNTRIES",
            term: Facet.Countries,
            metric: Metric.Repost,
            dataviz: getMapDataviz(),
          }),
        ]),
      ],
      ["query"]
    ),
  ],
};

export const Brands_Focus_Template: WizardTemplate = {
  id: "brands_focus",
  sections: [
    description(),
    section(
      [
        title(["INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::BRAND_AWARENESS"], "h1"),
        row([
          kpi({
            title: "INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::KPI_METRIC_REPOST",
            metric: Metric.Repost,
          }),
          kpi({
            title: "INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::KPI_METRIC_REACH",
            metric: Metric.Reach,
          }),
          kpi({
            title: "INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::KPI_METRIC_ENGAGEMENT",
            metric: Metric.Engagement,
          }),
        ]),
        row(
          [
            linechart({
              title:
                "INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::BRAND_VOLUME_OVER_TIME",
              metric: Metric.Repost,
            }),
            getWizardTermCard({
              title: "INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::BRANDS_RANKING",
              term: Facet.Focuses,
              metric: Metric.Repost,
              dataviz: getHorizontalBarChartDataviz({
                secondaryUnit: "evolution",
              }),
            }),
          ],
          "left"
        ),
        title(["INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::POPULAR_CONTENT"], "h1"),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::HASHTAGS",
            term: Facet.Hashtags,
            metric: Metric.Repost,
            dataviz: getCloudDataviz(),
          }),
          getWizardPostsCard({
            title: "INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::MOST_ENGAGING_PICTURES",
            sort: {
              on: Metric.Engagement,
            },
            dataviz: getPictureWallDataviz(),
          }),
        ]),
        row([
          getWizardUsersCard({
            title: "INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::MOST_ENGAGING_AUTHORS",
            sort: Metric.Engagement,
            dataviz: getTopAuthorsDataviz(),
          }),
          getWizardPostsCard({
            title:
              "INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::MOST_ENGAGING_OWNED_POSTS",
            sort: {
              on: Metric.Engagement,
            },
            dataviz: getTopPostsDataviz(),
          }),
        ]),
        title(
          ["INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::BRAND_VS_COMPETITORS"],
          "h1"
        ),
        title(
          ["INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::PLATFORM_DISTRIBUTION"],
          "h1"
        ),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::BRAND",
            term: Facet.Platforms,
            metric: Metric.Repost,
            dataviz: getHorizontalBarChartDataviz({ mainUnit: "percentage" }),
          }),
          getWizardTermCard(
            {
              title: "INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::ALL_BRANDS",
              term: Facet.Platforms,
              metric: Metric.Repost,
              dataviz: getHorizontalBarChartDataviz({ mainUnit: "percentage" }),
            },
            { without: ["query"] }
          ),
        ]),
        title(["INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::SENTIMENT"], "h1"),
        row([
          getWizardTermCard({
            title: "INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::BRAND",
            term: Facet.Tones,
            metric: Metric.Repost,
            dataviz: getHorizontalBarChartDataviz({ mainUnit: "percentage" }),
          }),
          getWizardTermCard(
            {
              title: "INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::ALL_BRANDS",
              term: Facet.Tones,
              metric: Metric.Repost,
              dataviz: getHorizontalBarChartDataviz({ mainUnit: "percentage" }),
            },
            { without: ["query"] }
          ),
        ]),
        // title(
        //   ["INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::CONVERSATIONAL_DRIVERS"],
        //   "h1"
        // ),
        // row([
        //   getWizardTermCard({
        //     title: "INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::BRAND",
        //     term: Facet.Focuses,
        //     metric: Metric.Repost,
        //     dataviz: getHorizontalBarChartDataviz(),
        //   }),
        //   getWizardTermCard(
        //     {
        //       title: "INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::ALL_BRANDS",
        //       term: Facet.Focuses,
        //       metric: Metric.Repost,
        //       dataviz: getHorizontalBarChartDataviz(),
        //     },
        //     { without: ["query"] }
        //   ),
        // ]),
        // title(["INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::SUB_DRIVERS"], "h1"),
        // row([
        //   getWizardTermCard({
        //     title: "INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::BRAND",
        //     term: Facet.Focuses,
        //     metric: Metric.Repost,
        //     dataviz: getHorizontalBarChartDataviz(),
        //   }),
        //   getWizardTermCard(
        //     {
        //       title: "INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::ALL_BRANDS",
        //       term: Facet.Focuses,
        //       metric: Metric.Repost,
        //       dataviz: getHorizontalBarChartDataviz(),
        //     }
        //   ),
        //   getWizardTermCard(
        //     {
        //       title: "INSIGHT_PAGE_TEMPLATE_BRANDS_FOCUS::ALL_BRANDS",
        //       term: Facet.Focuses,
        //       metric: Metric.Repost,
        //       dataviz: getHorizontalBarChartDataviz(),
        //     }
        //   ),
        // ]),
      ],
      ["query"]
    ),
  ],
};
